import { SignIn } from "@clerk/clerk-react";
import { useSearchParams } from "react-router-dom";
import { clerkEnabled } from "@/services/clerk";
import { REDIRECT_PARAM, safeRedirect } from "@/lib/redirects";
import { ClerkNotConfigured } from "@/components/ClerkNotConfigured";

/**
 * Where the Apply button sends somebody with no account.
 *
 * Clerk's combined flow, headed as a sign-up. It was the standalone `<SignUp>`
 * for a few hours and that could not handle the commonest case there is:
 * somebody who already has an account pressing Apply. Google returns an
 * identity Clerk already holds, the sign-up has to be "transferred" into a
 * sign-in, and that transfer failed with "Unable to complete action at this
 * time" — every time, for an account with a Google login and no password,
 * which is all three of ours.
 *
 * The combined flow does both directions natively, which is why `/sign-in`
 * never had the problem. The title is an override rather than a different
 * component: what changes when you arrive from Apply is what you are being
 * asked to do, not what the form can do.
 */
export default function SignUpPage() {
  // Read before the early return: hooks cannot be called conditionally.
  const [params] = useSearchParams();
  // Clerk takes a fixed URL, and where somebody belongs depends on whether
  // they are a member yet — which is not known until `me` answers. So it goes
  // to /after-sign-in, carrying the destination, and that route decides.
  const asked = params.get(REDIRECT_PARAM);
  const back =
    "/after-sign-in" +
    (asked ? `?${REDIRECT_PARAM}=${encodeURIComponent(safeRedirect(asked))}` : "");
  // An emailed invitation (a sponsored membership, an organization) links here
  // with the address it was sent to. Whatever is waiting is found by that
  // address, so it is typed in for them rather than left to be typed wrong.
  const email = params.get("email") ?? undefined;
  if (!clerkEnabled) return <ClerkNotConfigured />;
  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-12">
      {/* One line. It was a heading and three sentences explaining what the
          next screen is and why an account helps, above a card that already
          says "Create your account" — the reader is being told the same thing
          three ways before they have typed anything. */}
      <div className="mb-6 w-full max-w-[25rem] px-4 sm:px-0">
        <h1 className="text-2xl">Create your account</h1>
        <p className="mt-2 text-sm">An AFELL account is required for membership.</p>
        <p className="mt-1 text-sm">
          If you already have one, the same form signs you in.
        </p>
      </div>
      <SignIn
        routing="path"
        path="/sign-up"
        withSignUp
        signUpUrl="/sign-up"
        fallbackRedirectUrl={back}
        initialValues={email ? { emailAddress: email } : undefined}
        // Clerk's own header says "Continue to AFELL Directory", which is what
        // sent somebody pressing Apply looking for a door they had never been
        // through. `localization` is a provider-level prop, not a per-instance
        // one, and setting it there would rename /sign-in too — so the header
        // is hidden and the line above the card is the only one.
        appearance={{
          elements: {
            headerTitle: { display: "none" },
            headerSubtitle: { display: "none" },
          },
        }}
      />
    </div>
  );
}
